import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import SkillPickerModal from '../routines/SkillPickerModal';
import Toast from '../Toast';
import { ENERGY, MOOD, RPE_HINTS, routineLabel } from './diaryScales';
import './Diary.css';

const today = () => new Date().toISOString().slice(0, 10);
const pad = (n) => String(n).padStart(2, '0');
const HOURS = Array.from({ length: 24 }, (_, h) => pad(h));
const MINUTES = ['00', '15', '30', '45'];

// Meal time as [hour, minute] rounded to the nearest 15 minutes (never past 23:45).
function mealTimeOf(iso) {
  if (!iso) return ['', '00'];
  const d = new Date(iso);
  const total = Math.min(Math.round((d.getHours() * 60 + d.getMinutes()) / 15) * 15, 23 * 60 + 45);
  return [pad(Math.floor(total / 60)), pad(total % 60)];
}

let rowKey = 0;
const toRow = (s) => ({
  key: ++rowKey,
  skillId: s.skillId || null,
  name: s.name,
  count: s.count || 1,
  qualityNote: s.qualityNote || '',
  isRoutine: Boolean(s.isRoutine),
  gymnastRoutineId: s.gymnastRoutineId || null,
  fromSkill: s.fromSkill ?? null,
  toSkill: s.toSkill ?? null,
});

// One face scale as a radio group: "Energy: Good" etc. for screen readers.
function FaceScale({ legend, scale, value, onChange }) {
  return (
    <fieldset className="diary-scale">
      <legend>{legend}</legend>
      <div className="diary-scale__options" role="radiogroup" aria-label={legend}>
        {scale.map(f => (
          <button
            key={f.value}
            type="button"
            role="radio"
            aria-checked={value === f.value}
            aria-label={`${legend}: ${f.label}`}
            title={f.label}
            className={`diary-face${value === f.value ? ' selected' : ''}`}
            onClick={() => onChange(value === f.value ? null : f.value)}
          >
            {f.emoji}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

// Pick one of the gymnast's routines to track: the whole thing or a run of
// skills from it.
function RoutinePicker({ routines, onAdd, onCancel }) {
  const usable = routines.filter(r => r.skills.length > 0);
  const [routineId, setRoutineId] = useState(null);
  const [part, setPart] = useState(false);
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(1);
  // Routines may arrive after the picker opens, so default to the first.
  const routine = usable.find(r => r.id === routineId) || usable[0];

  if (usable.length === 0) {
    return (
      <div className="diary-routine-picker">
        <p className="diary-empty">No routines with skills yet. Your coach can add them.</p>
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>Close</button>
      </div>
    );
  }

  const choose = (id) => {
    setRoutineId(id);
    setFrom(1);
    setTo(1);
  };
  const add = () => onAdd(routine, part ? { fromSkill: Math.min(from, to), toSkill: Math.max(from, to) } : {});
  const skillOptions = routine.skills.map((sk, i) => (
    <option key={i} value={i + 1}>{i + 1}. {sk.name}</option>
  ));

  return (
    <div className="diary-routine-picker">
      <label className="diary-field">Routine
        <select className="form-control" value={routine.id} onChange={e => choose(e.target.value)}>
          {usable.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </label>
      <div className="diary-routine-picker__part" role="radiogroup" aria-label="How much of the routine">
        <label><input type="radio" checked={!part} onChange={() => setPart(false)} /> Whole routine</label>
        <label><input type="radio" checked={part} onChange={() => setPart(true)} /> Part of it</label>
      </div>
      {part && (
        <div className="diary-row">
          <label className="diary-field">From skill
            <select className="form-control" value={from} onChange={e => setFrom(Number(e.target.value))}>{skillOptions}</select>
          </label>
          <label className="diary-field">To skill
            <select className="form-control" value={to} onChange={e => setTo(Number(e.target.value))}>{skillOptions}</select>
          </label>
        </div>
      )}
      <div className="diary-list__actions">
        <button type="button" className="btn btn-primary btn-sm" onClick={add}>Add to session</button>
        <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// RPE 1–10 on a slider. Starts "Not set": a range input always shows a value,
// so a tap (or Space/Enter) commits whatever it shows, and Clear goes back to
// unset. Tabbing in fires keyup too, so other keys don't commit.
function RpeSlider({ value, onChange }) {
  const shown = value ?? 5;
  const commit = () => { if (value == null) onChange(shown); };
  return (
    <div className="diary-rpe">
      <div className="diary-rpe__head">
        <label htmlFor="diary-rpe">How hard was it? (RPE)</label>
        <span className="diary-rpe__value" aria-live="polite">
          {value == null ? 'Not set' : `${value} – ${RPE_HINTS[value]}`}
        </span>
      </div>
      <input
        id="diary-rpe"
        type="range" min="1" max="10" step="1"
        className={`diary-rpe__slider${value == null ? ' diary-rpe__slider--unset' : ''}`}
        value={shown}
        aria-valuetext={value == null ? 'Not set' : `${value}, ${RPE_HINTS[value]}`}
        onChange={e => onChange(Number(e.target.value))}
        onPointerUp={commit}
        onKeyUp={e => { if (e.key === ' ' || e.key === 'Enter') commit(); }}
      />
      <div className="diary-rpe__scale" aria-hidden="true"><span>1 very easy</span><span>10 maximal</span></div>
      {value != null && (
        <button type="button" className="btn btn-xs btn-outline" onClick={() => onChange(null)}>Clear</button>
      )}
    </div>
  );
}

// Create (no entry) or edit (entry given) a diary entry. Saving is allowed at
// any point, so a gymnast can fill in "before" now and "after" later.
export default function DiaryEntryForm({ gymnastId: gymnastIdProp, entry, onSaved, onCancel }) {
  const gymnastId = gymnastIdProp || entry?.gymnastId;
  const [date, setDate] = useState(entry?.date || today());
  const [energy, setEnergy] = useState(entry?.energy ?? null);
  const [mood, setMood] = useState(entry?.mood ?? null);
  const [meal, setMeal] = useState(entry?.lastMealDescription || '');
  const [[mealHour, mealMinute], setMealTime] = useState(() => mealTimeOf(entry?.lastMealAt));
  const [factors, setFactors] = useState(entry?.factors || '');
  const [goal, setGoal] = useState(entry?.sessionGoal || '');
  const [skills, setSkills] = useState((entry?.skills || []).map(toRow));
  const [duration, setDuration] = useState(entry?.durationMinutes ?? '');
  const [rpe, setRpe] = useState(entry?.rpe ?? null);
  const [library, setLibrary] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [routinePickerOpen, setRoutinePickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    axios.get('/api/skills').then(res => setLibrary(res.data || [])).catch(() => setLibrary([]));
  }, []);

  useEffect(() => {
    if (!gymnastId) return;
    axios.get(`/api/gymnast-routines/gymnast/${gymnastId}`)
      .then(res => setRoutines(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRoutines([]));
  }, [gymnastId]);

  const addSkill = (skillId, customName) => {
    const lib = skillId ? library.find(s => s.id === skillId) : null;
    if (skillId && !lib) return false;
    setSkills(rows => [...rows, toRow({ skillId, name: lib ? lib.name : customName })]);
    return true;
  };
  const addRoutine = (routine, range) => {
    setSkills(rows => [...rows, toRow({ isRoutine: true, gymnastRoutineId: routine.id, name: routine.name, ...range })]);
    setRoutinePickerOpen(false);
  };
  const updateRow = (key, patch) => setSkills(rows => rows.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key) => setSkills(rows => rows.filter(r => r.key !== key));

  const save = async () => {
    setSaving(true);
    const body = {
      date,
      energy,
      mood,
      lastMealDescription: meal,
      lastMealAt: mealHour ? new Date(`${date}T${mealHour}:${mealMinute}`).toISOString() : null,
      factors,
      sessionGoal: goal,
      durationMinutes: duration === '' ? null : Number(duration),
      rpe,
      skills: skills.map(({ key, count, ...row }) => ({ ...row, count: Number(count) })),
    };
    try {
      // How the entry looks if it's saved on the phone until there's signal
      const offlinePreview = {
        ...(entry || { gymnastId }),
        ...body,
        skills: body.skills.map((s, i) => ({ ...s, id: `row-${i}`, order: i + 1, figNotation: null })),
        pending: undefined,
        failed: undefined,
      };
      const res = entry
        ? await axios.put(`/api/training-diary/entries/${entry.id}`, body, { offlinePreview })
        : await axios.post(`/api/training-diary/gymnasts/${gymnastId}/entries`, body, { offlinePreview });
      onSaved(res.data);
    } catch (err) {
      setToast({ id: Date.now(), type: 'error', message: err.response?.data?.error || 'Could not save the entry' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content diary-form" role="dialog" aria-label={entry ? 'Edit session' : 'Log a session'}>
        <h3>{entry ? 'Edit session' : 'Log a session'}</h3>

        <div className="diary-row diary-form__top">
          <label className="diary-field">Date
            <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
          </label>
          <label className="diary-field">Duration (minutes)
            <input type="number" min="1" max="600" className="form-control" value={duration} onChange={e => setDuration(e.target.value)} />
          </label>
        </div>

        <section className="diary-section diary-section--before" aria-labelledby="diary-before">
          <h4 id="diary-before">Before the session</h4>
          <FaceScale legend="Energy" scale={ENERGY} value={energy} onChange={setEnergy} />
          <FaceScale legend="Mood" scale={MOOD} value={mood} onChange={setMood} />
          <div className="diary-row">
            <label className="diary-field">Last meal
              <input className="form-control" value={meal} maxLength={200} onChange={e => setMeal(e.target.value)} placeholder="What did you eat?" />
            </label>
            <fieldset className="diary-field diary-field--time">
              <legend>When</legend>
              <div className="diary-time">
                <select className="form-control" aria-label="Meal hour" value={mealHour}
                  onChange={e => setMealTime([e.target.value, e.target.value ? mealMinute : '00'])}>
                  <option value="">--</option>
                  {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <span aria-hidden="true">:</span>
                <select className="form-control" aria-label="Meal minutes" value={mealMinute} disabled={!mealHour}
                  onChange={e => setMealTime([mealHour, e.target.value])}>
                  {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </fieldset>
          </div>
          <label className="diary-field">Anything affecting training today?
            <textarea className="form-control" rows={2} value={factors} maxLength={1000} onChange={e => setFactors(e.target.value)} placeholder="Injuries, feeling unwell, exams..." />
          </label>
          <label className="diary-field">Session goal
            <input className="form-control" value={goal} maxLength={500} onChange={e => setGoal(e.target.value)} />
          </label>
        </section>

        <section className="diary-section diary-section--during" aria-labelledby="diary-during">
          <h4 id="diary-during">During the session</h4>
          <div className="diary-skills">
            {skills.map(r => {
              const label = r.isRoutine ? routineLabel(r) : r.name;
              return (
                <div key={r.key} className={`diary-skill-row${r.isRoutine ? ' diary-skill-row--routine' : ''}`}>
                  <span className="diary-skill-row__name">
                    {r.isRoutine && <span className="diary-tag">Routine</span>}{label}
                  </span>
                  <div className="diary-stepper">
                    <button type="button" aria-label={`Fewer ${label}`} onClick={() => updateRow(r.key, { count: Math.max(1, Number(r.count) - 1) })}>−</button>
                    <input
                      type="number" min="1" max="999" className="form-control"
                      aria-label={`How many times: ${label}`}
                      value={r.count}
                      onChange={e => updateRow(r.key, { count: e.target.value })}
                    />
                    <button type="button" aria-label={`More ${label}`} onClick={() => updateRow(r.key, { count: Number(r.count) + 1 })}>+</button>
                  </div>
                  <input
                    className="form-control diary-skill-row__note"
                    aria-label={`Quality note for ${label}`}
                    placeholder="How did it go?"
                    value={r.qualityNote}
                    maxLength={500}
                    onChange={e => updateRow(r.key, { qualityNote: e.target.value })}
                  />
                  <button type="button" className="btn btn-xs btn-outline" aria-label={`Remove ${label}`} onClick={() => removeRow(r.key)}>✕</button>
                </div>
              );
            })}
            {routinePickerOpen && (
              <RoutinePicker routines={routines} onAdd={addRoutine} onCancel={() => setRoutinePickerOpen(false)} />
            )}
            <div className="diary-list__actions">
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setPickerOpen(true)}>Add skills</button>
              {!routinePickerOpen && (
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setRoutinePickerOpen(true)}>Add routine</button>
              )}
            </div>
          </div>
        </section>

        <section className="diary-section diary-section--after" aria-labelledby="diary-after">
          <h4 id="diary-after">After the session</h4>
          <RpeSlider value={rpe} onChange={setRpe} />
        </section>

        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>Save</button>
        </div>

        {pickerOpen && (
          <SkillPickerModal
            availableSkills={library}
            mode="add"
            allowCustom
            onSave={(skillId, name) => addSkill(skillId, name)}
            onCancel={() => setPickerOpen(false)}
          />
        )}
        {toast && <Toast key={toast.id} message={toast.message} type={toast.type} onDismiss={dismissToast} />}
      </div>
    </div>
  );
}
